import { FoodsToggleButton, FoodsToggleButtonType, FormBox } from "@foodpilot/foods";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import { ProductFormType } from "../ProductForm.types";
import { useDistributionReference } from "src/api/sure-api/useDistributionReference";
import { Box } from "@mui/material";
import { useApplicationParameters } from "src/api/client-api/appParams/useApplicationParameters";
import { useDistributions } from "src/api/client-api/distribution/useDistributions";
import { formatFieldLabel } from "src/components/EntityForm/FoodsFieldPrimitives";

type DistributionSelection = {
    selected?: FoodsToggleButtonType;
    choices: FoodsToggleButtonType[];
};

type Page5Props = {
    product: ProductFormType;
    setProduct: (newProduct: ProductFormType) => void;
};
export const Page5 = (props: Page5Props) => {
    const { t } = useTranslation();
    const { product, setProduct } = props;
    const { isFeatureActivated } = useApplicationParameters();
    const isManagedDistributionEnabled = isFeatureActivated("FS-manage-my-distribution");
    const { distributionReference } = useDistributionReference();
    const { distributions } = useDistributions({ enabled: isManagedDistributionEnabled });

    if (isManagedDistributionEnabled) {
        const selectionState = distributions.reduce<DistributionSelection>(
            (previousValue, distribution) => {
                const choice = {
                    id: distribution.id,
                    label: distribution.name,
                    onClick: () => {
                        setProduct({
                            ...product,
                            distribution,
                            distributionReferenceId: null,
                        });
                    },
                };

                if (distribution.id === product.distribution?.id) {
                    previousValue.selected = choice;
                }

                previousValue.choices.push(choice);
                return previousValue;
            },
            { selected: undefined, choices: [] },
        );

        return (
            <FormBox sx={{ display: "flex", flexDirection: "column" }}>
                <Box sx={{ marginBottom: "10px" }}>
                    <Typography variant="h3">
                        {formatFieldLabel(t("product.distribution.mode_label", "Product distribution method:"))}
                    </Typography>
                </Box>
                <FoodsToggleButton
                    initialSelected={selectionState["selected"]}
                    buttons={selectionState["choices"]}
                    buttonGroup={true}
                />
            </FormBox>
        );
    }

    if (distributionReference === undefined) {
        return <div>{t("product.distribution.no_methods_available", "No distribution methods available")}</div>;
    }

    const selectionState = distributionReference.reduce<DistributionSelection>(
        (previousValue, distRef) => {
            const choice = {
                id: distRef.id,
                label: distRef.label,
                onClick: () => {
                    setProduct({
                        ...product,
                        distribution: null,
                        distributionReferenceId: distRef.id,
                    });
                },
            };

            if (distRef.id === product.distributionReferenceId) {
                previousValue.selected = choice;
            }

            previousValue.choices.push(choice);
            return previousValue;
        },
        { selected: undefined, choices: [] },
    );

    return (
        <FormBox sx={{ display: "flex", flexDirection: "column" }}>
            <Box sx={{ marginBottom: "10px" }}>
                <Typography variant="h3">
                    {formatFieldLabel(t("product.distribution.mode_label", "Product distribution method:"))}
                </Typography>
            </Box>
            <FoodsToggleButton
                initialSelected={selectionState["selected"]}
                buttons={selectionState["choices"]}
                buttonGroup={true}
            />
        </FormBox>
    );
};
